import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader, Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { fivemApi } from '../../api/fivem.api'
import { linksApi } from '../../api/links.api'
import { FiveMServerFormModal } from './FiveMServerFormModal'
import { ExternalLinkFormModal } from './ExternalLinkFormModal'
import type { FiveMServer, ExternalLink } from '../../api/types'

function FiveMServerCard({ server, onEdit }: { server: FiveMServer; onEdit: () => void }) {
  const { status } = server
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <Badge variant={status.online ? 'success' : 'danger'} dot>
          {status.online ? 'Online' : 'Offline'}
        </Badge>
        <strong style={{ flex: 1 }}>{server.name}</strong>
        <PermissionGate anyOf={['fivem.manage']}>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Bearbeiten
          </Button>
        </PermissionGate>
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
        {server.host}:{server.port}
      </div>
      {status.online ? (
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-sm)' }}>
          {status.hostname ? <div style={{ color: 'var(--text-secondary)' }}>{status.hostname}</div> : null}
          <div>
            👥 {status.clients ?? '—'} / {status.maxClients ?? '—'} Spieler
          </div>
          {status.mapname ? <div style={{ color: 'var(--text-muted)' }}>Map: {status.mapname}</div> : null}
        </div>
      ) : (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          Server nicht erreichbar oder offline.
        </div>
      )}
    </Card>
  )
}

function LinkCard({ link, onEdit }: { link: ExternalLink; onEdit: () => void }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--text-lg)' }}>{link.icon || '🔗'}</span>
        <a href={link.url} target="_blank" rel="noreferrer noopener" style={{ flex: 1, fontWeight: 600 }}>
          {link.label}
        </a>
        <PermissionGate anyOf={['links.manage']}>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Bearbeiten
          </Button>
        </PermissionGate>
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', marginTop: 4, wordBreak: 'break-all' }}>{link.url}</div>
    </Card>
  )
}

export function ServerPage() {
  const [formServer, setFormServer] = useState<FiveMServer | null | undefined>(undefined)
  const [formLink, setFormLink] = useState<ExternalLink | null | undefined>(undefined)

  const serversQuery = useQuery({
    queryKey: ['fivem', 'servers'],
    queryFn: async () => (await fivemApi.listServers()).data,
    refetchInterval: 15_000,
  })
  const linksQuery = useQuery({ queryKey: ['links'], queryFn: async () => (await linksApi.list()).data })

  const servers = serversQuery.data ?? []
  const links = linksQuery.data ?? []

  return (
    <div>
      <PageHeader title="Server" subtitle="FiveM-Server-Status und schnelle Links zu externen Tools." />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
        <h3 style={{ margin: 0 }}>FiveM-Server</h3>
        <PermissionGate anyOf={['fivem.manage']}>
          <Button size="sm" variant="primary" onClick={() => setFormServer(null)}>
            + Server hinzufügen
          </Button>
        </PermissionGate>
      </div>

      {servers.length === 0 ? (
        <EmptyState icon="🎮" title="Noch kein FiveM-Server hinterlegt" description="Trage einen Server mit IP und Port ein, um den Status zu sehen." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
          {servers.map((server) => (
            <FiveMServerCard key={server.id} server={server} onEdit={() => setFormServer(server)} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
        <h3 style={{ margin: 0 }}>Links</h3>
        <PermissionGate anyOf={['links.manage']}>
          <Button size="sm" variant="primary" onClick={() => setFormLink(null)}>
            + Link hinzufügen
          </Button>
        </PermissionGate>
      </div>

      {links.length === 0 ? (
        <EmptyState icon="🔗" title="Noch keine Links" description="Z.B. das Pterodactyl-Panel oder den Discord-Server verlinken." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
          {links.map((link) => (
            <LinkCard key={link.id} link={link} onEdit={() => setFormLink(link)} />
          ))}
        </div>
      )}

      {formServer !== undefined ? <FiveMServerFormModal server={formServer} onClose={() => setFormServer(undefined)} /> : null}
      {formLink !== undefined ? <ExternalLinkFormModal link={formLink} onClose={() => setFormLink(undefined)} /> : null}
    </div>
  )
}
